import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Circle, Spinner, View, XStack, YStack, useMedia } from 'tamagui'
import { NavigationControl } from '@components/NavigationControl'
import { Animated } from 'react-native'
import { useOpacityAnimation } from '@hooks/useOpacityAnimation'
import { LinearGradient } from 'expo-linear-gradient'

// Hide the horizontal scrollbar on WebKit (no project .css files). Other
// scroll/snap styles are applied imperatively on the track element below.
let scrollbarRuleInjected = false
function ensureScrollbarHidden() {
  if (scrollbarRuleInjected || typeof document === 'undefined') return
  scrollbarRuleInjected = true
  const style = document.createElement('style')
  style.textContent = '.hs-slider-track::-webkit-scrollbar{display:none}'
  document.head.appendChild(style)
}

export interface ISliderProps {
  slidesTotalNumber: number
  displaySliderControls: boolean
  displaySliderControlsHorizontal?: boolean
  slidesPerView: number
  paddingRight?: number | string
  sliderTitle?: React.ReactNode
  sliderTitleContainerStyles?: React.CSSProperties
  slidesSpacing?: number
  navigationControlVariation?: 'chevron' | 'dots'
  navigationControlSize?: number
  children: React.ReactNode
  loadMoreContent?: (args?: string | number) => Promise<void> | void
  hasMore?: boolean
  fullScreen?: boolean
  autoPlay?: boolean
  autoPlayInterval?: number
  displayIndicators?: boolean
  isCarousel?: boolean
  marginBottomToSliderControls?: number | string
  /** Inset (px) applied to the title and to the first/last slide so the track
   * bleeds to the screen edges while content stays aligned to this offset. */
  contentInset?: number
}

export function Slider({
  children,
  sliderTitle,
  slidesPerView,
  displaySliderControls = false,
  slidesSpacing = 24,
  slidesTotalNumber,
  loadMoreContent,
  hasMore = true,
  navigationControlVariation = 'chevron',
  displaySliderControlsHorizontal = false,
  navigationControlSize = 32,
  contentInset = 0,
}: ISliderProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [contentsLength, setContentsLength] = useState(slidesTotalNumber)
  const [isSliderMoving, setIsSliderMoving] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [navState, setNavState] = useState({ first: true, last: false })
  const isLoadingMoreRef = useRef(false)
  const loadMoreContentRef = useRef(loadMoreContent)
  const indexPaceRef = useRef(0)
  const hasMoreRef = useRef(hasMore)
  const currentIndexRef = useRef(0)
  const movingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const media = useMedia()
  const { interpolatedOpacity, startAnimation } = useOpacityAnimation()

  useEffect(() => {
    setContentsLength(slidesTotalNumber)
  }, [slidesTotalNumber])

  useEffect(() => {
    loadMoreContentRef.current = loadMoreContent
  }, [loadMoreContent])

  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  // Base scroll styles applied imperatively (no .css file). scroll-padding keeps
  // snap aligned at the inset rather than the bleeding edge.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    ensureScrollbarHidden()
    el.style.overflowX = 'auto'
    el.style.overflowY = 'hidden'
    el.style.scrollSnapType = 'x proximity'
    el.style.scrollbarWidth = 'none'
    el.style.scrollPaddingLeft = `${contentInset}px`
    el.style.scrollPaddingRight = `${contentInset}px`
  }, [contentInset])

  // Snap-align each slide; the last aligns to the end so the slider can rest at
  // the very end without leaving a trailing gap.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const slides = Array.from(el.children) as HTMLElement[]
    slides.forEach((slide, index) => {
      slide.style.scrollSnapAlign =
        index === slides.length - 1 ? 'end' : 'start'
      slide.style.flexShrink = '0'
    })
  }, [contentsLength])

  const indexPace = useMemo(() => {
    return Math.max(1, Math.floor(slidesPerView))
  }, [slidesPerView])

  indexPaceRef.current = indexPace
  currentIndexRef.current = currentIndex

  const calculateContainerTitleGap = () => {
    if (media.gtLg) return 160
    if (media.gtMd) return 80
    if (media.gtSm) return 139
    return 16
  }

  const isSliderVariationChevron = navigationControlVariation === 'chevron'
  const dotsQuantityToDisplay = Math.max(
    1,
    Math.ceil(contentsLength / indexPace),
  )
  const canDisplayDotsControl = media.gtMd && dotsQuantityToDisplay <= 5
  const dotIndexHighlight = Math.ceil(currentIndex / indexPace)
  const chevronIconSize = media.gtSm ? 24 : 16

  const navigationControl = navState

  const shouldHighlightDot = (index: number) => {
    return index === dotIndexHighlight ? '$icon-primary' : '$icon-secondary'
  }

  const getSlideEls = useCallback((): HTMLElement[] => {
    const el = scrollRef.current
    if (!el) return []
    return Array.from(el.children) as HTMLElement[]
  }, [])

  const getStride = useCallback(
    (slides: HTMLElement[]): number => {
      if (slides.length >= 2) {
        const stride = slides[1].offsetLeft - slides[0].offsetLeft
        return stride > 0 ? stride : slides[0].offsetWidth + slidesSpacing
      }
      if (slides.length === 1) return slides[0].offsetWidth + slidesSpacing
      return 1
    },
    [slidesSpacing],
  )

  const triggerLoadMore = useCallback(
    async (position: number, slidesLength: number) => {
      if (isLoadingMoreRef.current) return
      if (!hasMoreRef.current) return
      if (slidesLength === 0) return
      const pace = indexPaceRef.current
      const remainingSlides = slidesLength - (pace + position)
      const halfwayReached = position >= slidesLength / 2
      if (!(remainingSlides <= pace || halfwayReached)) return
      const fn = loadMoreContentRef.current
      if (!fn) return
      isLoadingMoreRef.current = true
      setIsLoadingMore(true)
      try {
        await fn()
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    },
    [],
  )

  const updateFromScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const slides = getSlideEls()
    const stride = getStride(slides)
    const index = stride > 0 ? Math.round(el.scrollLeft / stride) : 0
    setCurrentIndex(index)
    setNavState({
      first: el.scrollLeft <= 1,
      last: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
    })
    triggerLoadMore(index, slides.length)
  }, [getSlideEls, getStride, triggerLoadMore])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let raf = 0

    const onScroll = () => {
      setIsSliderMoving(true)
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(updateFromScroll)
      clearTimeout(movingTimeoutRef.current)
      movingTimeoutRef.current = setTimeout(() => setIsSliderMoving(false), 150)
    }

    let dragging = false
    let startX = 0
    let startLeft = 0
    let moved = false
    let lastX = 0
    let lastT = 0
    let velocity = 0
    let momentumRaf = 0

    // Animate scrollLeft to a target, then snap to the nearest slide and
    // restore scroll-snap. Uses rAF (instant per-frame writes) because animated
    // scrollTo can be reset by re-renders during loadMore.
    const settleTo = (projected: number) => {
      const slides = getSlideEls()
      const maxScroll = el.scrollWidth - el.clientWidth
      const clampedProjected = Math.max(0, Math.min(maxScroll, projected))
      // Nearest slide (inset-aligned), clamped to the scrollable range. Snaps
      // past the end collapse to maxScroll, so the end is reachable to show the
      // last card fully — while earlier snaps stay reachable when dragging back.
      let target = clampedProjected
      let best = Infinity
      slides.forEach((s) => {
        const snapPos = Math.max(
          0,
          Math.min(maxScroll, s.offsetLeft - contentInset),
        )
        const d = Math.abs(snapPos - clampedProjected)
        if (d < best) {
          best = d
          target = snapPos
        }
      })
      const from = el.scrollLeft
      const distance = target - from
      const duration = 320
      let startTs = 0
      const animate = (ts: number) => {
        if (!startTs) startTs = ts
        const p = Math.min(1, (ts - startTs) / duration)
        const ease = 1 - Math.pow(1 - p, 3)
        el.scrollLeft = from + distance * ease
        if (p < 1) {
          momentumRaf = requestAnimationFrame(animate)
        } else {
          el.style.scrollSnapType = 'x proximity'
        }
      }
      cancelAnimationFrame(momentumRaf)
      momentumRaf = requestAnimationFrame(animate)
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      dragging = true
      moved = false
      startX = e.clientX
      startLeft = el.scrollLeft
      lastX = e.clientX
      lastT = e.timeStamp
      velocity = 0
      cancelAnimationFrame(momentumRaf)
      // disable snap during drag so it tracks the cursor and we control settle
      el.style.scrollSnapType = 'none'
      el.style.cursor = 'grabbing'
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return
      const dx = e.clientX - startX
      if (Math.abs(dx) > 3) moved = true
      el.scrollLeft = startLeft - dx
      const dt = e.timeStamp - lastT
      if (dt > 0) {
        // px/ms; moving cursor right (dx>0) decreases scrollLeft
        velocity = -(e.clientX - lastX) / dt
        lastX = e.clientX
        lastT = e.timeStamp
      }
    }
    const onPointerUp = () => {
      if (!dragging) return
      dragging = false
      el.style.cursor = 'grab'
      if (moved) {
        // project momentum from release velocity, then settle to nearest slide
        const projected = el.scrollLeft + velocity * 180
        settleTo(projected)
        // swallow the click triggered by the drag release so cards don't open
        const swallow = (ev: Event) => {
          ev.stopPropagation()
          ev.preventDefault()
          el.removeEventListener('click', swallow, true)
        }
        el.addEventListener('click', swallow, true)
        setTimeout(() => el.removeEventListener('click', swallow, true), 60)
      } else {
        el.style.scrollSnapType = 'x proximity'
      }
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)

    updateFromScroll()

    return () => {
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      if (raf) cancelAnimationFrame(raf)
      cancelAnimationFrame(momentumRaf)
      clearTimeout(movingTimeoutRef.current)
    }
  }, [updateFromScroll, getSlideEls, contentInset])

  // Recompute index/nav state whenever the number of slides changes (e.g. after
  // loadMore appends content) so chevrons/dots reflect the new totals.
  useEffect(() => {
    const id = requestAnimationFrame(updateFromScroll)
    return () => cancelAnimationFrame(id)
  }, [contentsLength, updateFromScroll])

  const moveSlider = useCallback(
    (direction: 'left' | 'right') => {
      const el = scrollRef.current
      if (!el) return
      const slides = getSlideEls()
      if (!slides.length) return
      const pace = indexPaceRef.current
      const index = currentIndexRef.current
      const target =
        direction === 'left'
          ? Math.max(0, index - pace)
          : Math.min(slides.length - 1, index + pace)
      const left = Math.max(0, (slides[target]?.offsetLeft ?? 0) - contentInset)
      setIsSliderMoving(true)
      el.scrollTo({ left, behavior: 'smooth' })
    },
    [getSlideEls, contentInset],
  )

  useEffect(() => {
    startAnimation(displaySliderControlsHorizontal)
  }, [displaySliderControlsHorizontal, startAnimation])

  return (
    <YStack gap="$spacing-inset-xs" testID="slider">
      <XStack
        justifyContent="space-between"
        gap={calculateContainerTitleGap()}
        position="relative"
        alignItems="center"
        paddingLeft={contentInset}
        paddingRight={contentInset}
      >
        {sliderTitle}
        {displaySliderControls && (
          <XStack
            alignItems="center"
            $gtMd={{ width: '10%', justifyContent: 'flex-end' }}
            testID="slider-controls"
            gap="$spacing-inset-nano"
          >
            {isLoadingMore && (
              <Spinner
                size="small"
                color="$accent"
                testID="slider-loading-spinner"
              />
            )}
            {isSliderVariationChevron ? (
              <>
                <NavigationControl
                  action={() => moveSlider('left')}
                  chevronDirection="left"
                  disabled={navigationControl.first}
                  size={chevronIconSize}
                />
                <NavigationControl
                  action={() => moveSlider('right')}
                  chevronDirection="right"
                  disabled={navigationControl.last}
                  size={chevronIconSize}
                />
              </>
            ) : (
              <>
                {canDisplayDotsControl && (
                  <XStack gap={8} alignSelf="flex-end" paddingRight={50}>
                    {Array.from({ length: dotsQuantityToDisplay }).map(
                      (_, index) => (
                        <Circle
                          key={index}
                          size={6}
                          backgroundColor={shouldHighlightDot(index)}
                          hoverStyle={{
                            cursor: 'pointer',
                          }}
                          onPress={() => {
                            const direction =
                              index > dotIndexHighlight ? 'right' : 'left'
                            moveSlider(direction)
                          }}
                        />
                      ),
                    )}
                  </XStack>
                )}
              </>
            )}
          </XStack>
        )}
      </XStack>

      <View position="relative" width="100%">
        {displaySliderControlsHorizontal && !isSliderMoving && (
          <Animated.View
            style={{
              opacity: media.gtSm ? interpolatedOpacity : 1,
              zIndex: 1000,
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              overflow: 'hidden',
            }}
            pointerEvents="box-none"
          >
            {!navigationControl.first && (
              <View
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                width={96}
                justifyContent="center"
                pointerEvents="box-none"
              >
                <LinearGradient
                  pointerEvents="none"
                  colors={['rgba(0,0,0,0.86)', 'transparent']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{
                    zIndex: 999,
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                  }}
                />
                <NavigationControl
                  zIndex={1000}
                  alignSelf="flex-start"
                  onHoverIn={() => null}
                  onHoverOut={() => null}
                  isCarousel
                  chevronColor="$icon-soft"
                  action={() => moveSlider('left')}
                  chevronDirection="left"
                  disabled={navigationControl.first}
                  size={navigationControlSize}
                  style={{
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
              </View>
            )}
            {!navigationControl.last && (
              <View
                position="absolute"
                top={0}
                bottom={0}
                right={0}
                width={96}
                justifyContent="center"
                alignItems="flex-end"
                pointerEvents="box-none"
              >
                <LinearGradient
                  pointerEvents="none"
                  colors={['transparent', 'rgba(0,0,0,0.86)']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{
                    zIndex: 999,
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                  }}
                />
                <NavigationControl
                  zIndex={1000}
                  alignSelf="flex-end"
                  chevronColor="$icon-soft"
                  onHoverIn={() => null}
                  onHoverOut={() => null}
                  action={() => moveSlider('right')}
                  chevronDirection="right"
                  isCarousel
                  disabled={navigationControl.last}
                  size={navigationControlSize}
                  style={{
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
              </View>
            )}
          </Animated.View>
        )}
        <XStack
          ref={scrollRef as never}
          className="hs-slider-track"
          gap={slidesSpacing}
          paddingLeft={contentInset}
          paddingRight={contentInset}
          cursor="grab"
          userSelect="none"
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(
                child as React.ReactElement<{
                  className?: string
                  isslidermoving?: string
                }>,
                {
                  className:
                    `${(child.props as { className?: string }).className || ''} hs-slide`.trim(),
                  isslidermoving: isSliderMoving.toString(),
                },
              )
            }
            return child
          })}
        </XStack>
      </View>
    </YStack>
  )
}
